import type { Polygon } from 'geojson';
import { geographicLasso } from '../selection/lasso';

export interface ImportRectangleState {
  readonly drawing: boolean;
  readonly rectangle: Polygon | null;
  readonly onRectangle: (rectangle: Polygon | null) => void;
  readonly onEscape: () => void;
}

interface RectangleMap {
  unproject(point: [number, number]): { readonly lng: number; readonly lat: number };
  project(coordinate: [number, number]): { readonly x: number; readonly y: number };
  readonly dragPan?: { disable(): void; enable(): void };
}

/** A transient pointer overlay. It never creates a map source, annotation or saved project item. */
export class ImportRectangleInteraction {
  readonly #canvas: HTMLElement;
  readonly #map: RectangleMap;
  readonly #overlay: SVGSVGElement;
  readonly #path: SVGPathElement;
  #state: ImportRectangleState;
  #start: { x: number; y: number } | null = null;
  #pointer: number | null = null;
  constructor(
    canvas: HTMLElement,
    container: HTMLElement,
    map: RectangleMap,
    state: ImportRectangleState,
  ) {
    this.#canvas = canvas;
    this.#map = map;
    this.#state = state;
    this.#overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.#overlay.classList.add('map-import-rectangle');
    this.#overlay.setAttribute('aria-hidden', 'true');
    this.#path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.#overlay.append(this.#path);
    container.append(this.#overlay);
    canvas.addEventListener('pointerdown', this.#down, true);
    canvas.addEventListener('pointermove', this.#move, true);
    canvas.addEventListener('pointerup', this.#up, true);
    canvas.addEventListener('pointercancel', this.#cancel, true);
    window.addEventListener('keydown', this.#key, true);
    this.update(state);
  }
  update(state: ImportRectangleState) {
    this.#state = state;
    if (!state.drawing) this.#release();
    this.#canvas.style.cursor = state.drawing ? 'crosshair' : '';
    this.redraw();
  }
  redraw() {
    const ring = this.#state.rectangle?.coordinates[0];
    if (this.#start) return;
    this.#path.setAttribute(
      'd',
      ring?.length
        ? ring
            .map((coordinate, index) => {
              const point = this.#map.project([coordinate[0]!, coordinate[1]!]);
              return `${index ? 'L' : 'M'}${point.x},${point.y}`;
            })
            .join(' ') + ' Z'
        : '',
    );
  }
  #point(event: PointerEvent) {
    const bounds = this.#canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(bounds.width, event.clientX - bounds.left)),
      y: Math.max(0, Math.min(bounds.height, event.clientY - bounds.top)),
    };
  }
  #down = (event: PointerEvent) => {
    if (!this.#state.drawing || event.button !== 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#start = this.#point(event);
    this.#pointer = event.pointerId;
    this.#canvas.setPointerCapture?.(event.pointerId);
    this.#map.dragPan?.disable();
  };
  #move = (event: PointerEvent) => {
    if (!this.#start || event.pointerId !== this.#pointer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const end = this.#point(event);
    const start = this.#start;
    this.#path.setAttribute(
      'd',
      `M${start.x},${start.y} L${end.x},${start.y} L${end.x},${end.y} L${start.x},${end.y} Z`,
    );
  };
  #up = (event: PointerEvent) => {
    if (!this.#start || event.pointerId !== this.#pointer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const start = this.#start;
    const end = this.#point(event);
    const polygon = geographicLasso(
      [start, { x: end.x, y: start.y }, end, { x: start.x, y: end.y }],
      (point) => this.#map.unproject([point.x, point.y]),
    );
    this.#release();
    this.#state.onRectangle(polygon);
  };
  #cancel = () => {
    this.#release();
    this.#state.onRectangle(null);
  };
  #key = (event: KeyboardEvent) => {
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      (event.target instanceof Element && event.target.closest('[data-app-dialog]'))
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#release();
    this.#state.onEscape();
  };
  #release() {
    if (this.#pointer !== null && this.#canvas.hasPointerCapture?.(this.#pointer))
      this.#canvas.releasePointerCapture(this.#pointer);
    this.#start = null;
    this.#pointer = null;
    this.#map.dragPan?.enable();
  }
  destroy() {
    this.#release();
    this.#canvas.style.cursor = '';
    this.#overlay.remove();
    this.#canvas.removeEventListener('pointerdown', this.#down, true);
    this.#canvas.removeEventListener('pointermove', this.#move, true);
    this.#canvas.removeEventListener('pointerup', this.#up, true);
    this.#canvas.removeEventListener('pointercancel', this.#cancel, true);
    window.removeEventListener('keydown', this.#key, true);
  }
}
